import React, { useState, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField, CircularProgress, Box, Typography, Chip } from '@mui/material';
import { Person as PersonIcon, AccountCircle as AccountIcon } from '@mui/icons-material';
import { accountsAPI } from '../services/apiService';

/**
 * AccountAutocomplete — Combined Customer Name + Account Search field
 *
 * Single text field that:
 *  - Accepts freeform customer name entry
 *  - Searches accounts while typing and shows matches as suggestions
 *  - Selecting a suggestion fires onAccountSelect(account) to auto-fill other fields
 *  - Shows a linked-account chip beneath the field when an account is selected
 *  - If no account is picked, behaves as a plain Customer Name text field
 *
 * Props:
 *  value              — current text value (customerName string)
 *  onChange(text)     — called on every keystroke
 *  onAccountSelect(a) — called when user picks an account from the dropdown
 *  linkedAccount      — currently linked account object (or null)
 *  onRemoveAccount    — called to unlink the account
 *  label, error, helperText, required, disabled, placeholder
 */
const AccountAutocomplete = ({
    value = '',
    onChange,
    onAccountSelect,
    linkedAccount,
    onRemoveAccount,
    label = 'Customer Name',
    disabled = false,
    error = false,
    helperText = '',
    required = false,
    placeholder = 'Enter name or search accounts…',
}) => {
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const debounceTimer = useRef(null);

    const handleInputChange = (_event, newInput, reason) => {
        if (reason === 'reset') return;
        if (onChange) onChange(newInput);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        if (newInput.length < 2) {
            setOptions([]);
            return;
        }

        debounceTimer.current = setTimeout(async () => {
            setIsLoading(true);
            try {
                const data = await accountsAPI.search(newInput);
                setOptions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Account search failed:', err);
                setOptions([]);
            } finally {
                setIsLoading(false);
            }
        }, 400);
    };

    const handleChange = (_event, selected, reason) => {
        if (reason === 'selectOption' && selected && typeof selected === 'object') {
            if (onChange) onChange(selected.name || '');
            if (onAccountSelect) onAccountSelect(selected);
            setOptions([]);
        } else if (reason === 'clear') {
            if (onChange) onChange('');
            if (onRemoveAccount) onRemoveAccount();
        }
    };

    return (
        <Box sx={{ width: '100%', minWidth: 230 }}>
            <Autocomplete
                freeSolo
                fullWidth
                options={options}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.name || '')}
                filterOptions={(x) => x}
                inputValue={value}
                onInputChange={handleInputChange}
                onChange={handleChange}
                loading={isLoading}
                disabled={disabled}
                noOptionsText="No accounts found"
                isOptionEqualToValue={(opt, val) =>
                    typeof val === 'object' ? opt.accountId === val?.accountId : false
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        label={label}
                        required={required}
                        error={error}
                        helperText={helperText}
                        margin="normal"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading ? <CircularProgress size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.accountId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                        <PersonIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
                        <Box>
                            <Typography variant="body2" fontWeight={500}>
                                {option.name}
                                {option.company && (
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {' · '}{option.company}
                                    </Typography>
                                )}
                            </Typography>
                            {option.phoneNumber && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {option.phoneNumber}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            />
            {linkedAccount ? (
                <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                    <AccountIcon fontSize="small" color="primary" />
                    <Chip
                        size="small"
                        label={[linkedAccount.name, linkedAccount.company].filter(Boolean).join(' · ')}
                        color="primary"
                        variant="outlined"
                        onDelete={onRemoveAccount}
                    />
                    {linkedAccount.discountType && linkedAccount.discountValue > 0 && (
                        <Chip
                            size="small"
                            label={linkedAccount.discountType === 'percent'
                                ? `${linkedAccount.discountValue}% off`
                                : `$${linkedAccount.discountValue} off`}
                            color="success"
                        />
                    )}
                </Box>
            ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, mb: 0.5, display: 'block', pl: 0.5 }}>
                    {placeholder}
                </Typography>
            )}
        </Box>
    );
};

export default AccountAutocomplete;
